import { CampaignType } from 'cio/models/campaign';

export type CampaignTypeTriggerName =
  | 'notSureTextOnly'
  | 'notSureWithVideo'
  | CampaignType.Behavioral
  | CampaignType.Transactional
  | CampaignType.Form
  | CampaignType.Date
  | CampaignType.Webhook;

export const CampaignTypeTrackingName: Record<CampaignTypeTriggerName, string> = {
  [CampaignType.Behavioral]: 'Segment',
  [CampaignType.Transactional]: 'Event',
  [CampaignType.Form]: 'Form',
  [CampaignType.Date]: 'Date',
  [CampaignType.Webhook]: 'Webhook',
  notSureTextOnly: 'Not Sure',
  notSureWithVideo: 'Not Sure',
};

// TODO: remove/refactor when moving completely to new Campaign Trigger UI: when removing "campaign_trigger_ui_new" feature flag

export interface CampaignTypeTriggerInfo {
  id: CampaignType | 'notSureTextOnly' | 'notSureWithVideo';
  title: string;
  description?: string[];
  icons?: { icon: string; title?: string }[];
  primaryLink: string;
  secondaryLink?: string;
}

export const campaignTypeTriggerInfo: Readonly<Record<CampaignTypeTriggerName, CampaignTypeTriggerInfo>> = {
  [CampaignType.Behavioral]: {
    id: CampaignType.Behavioral,
    title: 'Segment triggered campaigns',
    description: [
      'A segment is a series of conditions about people, their attributes, and the events they perform. People enter a segment when they match your conditions, and exit when they stop matching conditions. This option lets you trigger your campaign when people enter or exit segments. You can pick an existing segment, or set criteria for a new segment when you use this option.',
    ],
    icons: [
      {
        icon: 'lead-nurturing',
        title: 'Lead Nurturing',
      },
      {
        icon: 'drip-campaigns',
        title: 'Onboarding drip campaigns',
      },
      {
        icon: 'personalized-marketing',
        title: 'Personalized Marketing',
      },
    ],
    primaryLink: 'https://customer.io/docs/campaign-triggers/#condition-trigger',
  },
  [CampaignType.Transactional]: {
    id: CampaignType.Transactional,
    title: 'Event triggered campaigns',
    description: [
      "Event triggers help you respond to things your users do on your website or in your app with relevant messages. When your audience does something you find important, you can send an event representing that person's activity, and use that event to trigger your campaign. You can even reference event data using <a href='https://customer.io/docs/using-liquid/' class='button-link' target='_blank' rel='noopener noreferrer'>Liquid</a>. Event-based triggers are great for cart-abandonment emails and upcoming appointment reminders.",
    ],
    icons: [
      {
        icon: 'abandoned-cart',
        title: 'Abandoned cart messages',
      },
      {
        icon: 'reminder-bell',
        title: 'appointment reminders',
      },
      {
        icon: 'check-in-square',
        title: 'Confirmation Emails',
      },
    ],
    primaryLink: 'https://customer.io/docs/campaign-triggers/#event-trigger',
  },
  [CampaignType.Form]: {
    id: CampaignType.Form,
    title: 'Form triggered campaigns',
    description: [
      'With form triggers, you can take advantage of form submissions to trigger campaigns and do things like send messages to new leads to try to convert them to customers or users, or respond to user research surveys. You can use data from each form submission in messages, helping you automate relevant responses to people who fill out your forms.',
    ],
    icons: [
      {
        icon: 'responding',
        title: 'Responding to Leads',
      },
      {
        icon: 'research',
        title: 'User Research',
      },
      {
        icon: 'support',
        title: 'Support requests',
      },
    ],
    primaryLink: 'https://customer.io/docs/campaign-triggers/#form-trigger',
  },
  [CampaignType.Date]: {
    id: CampaignType.Date,
    title: 'Date triggered campaigns',
    description: [
      "If you store audience attributes containing dates—like people's birthdays or subscription dates—you can trigger campaigns based on those dates. When you use a date-based trigger, you can use the exact date, or you can offset it to message people before (or after) the date in question. This kind of trigger is great for recurring events like birthdays, anniversaries, subscription renewals, payment reminders, etc.",
    ],
    icons: [
      {
        icon: 'cake',
        title: 'Birthdays',
      },
      {
        icon: 'subscription-renewal',
        title: 'Subscription renewals',
      },
      {
        icon: 'anniversary',
        title: 'Anniversaries',
      },
    ],
    primaryLink: 'https://customer.io/docs/campaign-triggers/#date-trigger',
  },
  [CampaignType.Webhook]: {
    id: CampaignType.Webhook,
    title: 'Webhook triggered campaigns',
    description: [
      'While most triggers are based on things people do, webhook triggers are based on object data—like products or job postings.',
      'Webhook-triggered campaigns provide a low-code way to capture and associate individual pieces of data from outside services like Stripe or Greenhouse with multiple people in your audience.',
      'For example, you might send a webhook to Customer.io when a product comes back in stock. Then, in your webhook-triggered campaign, you can send an event to every person who was interested in that product, triggering a downstream campaign that lets people know that their product is back in stock.',
    ],
    icons: [
      {
        icon: 'automation',
        title: 'Automations',
      },
      {
        icon: 'integration',
        title: 'Integrations',
      },
      {
        icon: 'triggers',
        title: 'Custom Triggers',
      },
    ],
    primaryLink: 'https://customer.io/docs/campaign-triggers/#webhook-trigger',
  },
  notSureTextOnly: {
    id: 'notSureTextOnly',
    title: 'Not sure which trigger to pick?',
    primaryLink: 'https://community.customer.io/',
    secondaryLink: 'https://customer.io/docs/campaign-triggers/',
  },
  notSureWithVideo: {
    id: 'notSureWithVideo',
    title: 'Not sure which trigger to pick?',
    primaryLink: 'https://community.customer.io/',
    secondaryLink: 'https://customer.io/docs/campaign-triggers/',
  },
};
